import { visionLabApi } from '@/app/apiClient';
import {
  type InitiateMcapSubmissionInput,
  type McapIngest,
  type McapRecheckResponse,
  type SubmissionSession,
  type UploaderSubmissionPage,
  initiateMcapSubmissionInputSchema,
} from '@/features/mcap/types/contracts';
import type { ApiCallOptions } from '@/shared/api/client';

export async function initiateMcapSubmission(
  input: InitiateMcapSubmissionInput,
  options: ApiCallOptions = {},
): Promise<SubmissionSession> {
  const body = initiateMcapSubmissionInputSchema.parse(input);
  return visionLabApi.post<SubmissionSession>('/submissions', { ...options, json: body });
}

export async function beginResumableUpload(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<SubmissionSession> {
  return visionLabApi.post<SubmissionSession>(
    `/submissions/${encodeURIComponent(sessionId)}/resumable-begin`,
    { ...options, json: {} },
  );
}

export async function reportSubmissionProgress(
  sessionId: string,
  partNo: number,
  cumulativeBytes: string,
  options: ApiCallOptions = {},
): Promise<SubmissionSession> {
  return visionLabApi.post<SubmissionSession>(
    `/submissions/${encodeURIComponent(sessionId)}/report-progress`,
    { ...options, json: { partNo, cumulativeBytes } },
  );
}

export async function completeSubmission(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<SubmissionSession> {
  return visionLabApi.post<SubmissionSession>(
    `/submissions/${encodeURIComponent(sessionId)}/complete`,
    {
      ...options,
      json: {},
    },
  );
}

export async function fetchSubmission(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<SubmissionSession> {
  return visionLabApi.get<SubmissionSession>(
    `/submissions/${encodeURIComponent(sessionId)}`,
    options,
  );
}

/** One uploader-owned mixed-media page used only to recover a duplicate MCAP submission. */
export async function fetchUploaderSubmissionPage(
  uploaderId: string,
  { cursor }: { cursor?: string } = {},
  options: ApiCallOptions = {},
): Promise<UploaderSubmissionPage> {
  return visionLabApi.get<UploaderSubmissionPage>(
    `/submissions/by-uploader/${encodeURIComponent(uploaderId)}`,
    {
      ...options,
      searchParams: { limit: 500, cursor: cursor || undefined },
    },
  );
}

/** Backend/GCS-authoritative next byte for a resumed session. */
export async function fetchUploadOffset(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<{ offset: number }> {
  return visionLabApi.get<{ offset: number }>(
    `/submissions/${encodeURIComponent(sessionId)}/upload-offset`,
    { ...options, signal: options.signal },
  );
}

export async function fetchMcapIngest(
  sessionId: string,
  options: ApiCallOptions = {},
): Promise<McapIngest> {
  return visionLabApi.get<McapIngest>(
    `/submissions/${encodeURIComponent(sessionId)}/mcap-ingest`,
    options,
  );
}

export async function recheckMcapIngest(
  sessionId: string,
  requestId: string,
  failedTaskId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<McapRecheckResponse> {
  return visionLabApi.post<McapRecheckResponse>(
    `/submissions/${encodeURIComponent(sessionId)}/mcap-ingest/recheck`,
    { ...options, json: { requestId, failedTaskId, reason } },
  );
}

/** Administrative retry of the same immutable MCAP source after an input-compatibility fix. */
export async function adminRetryMcapIngest(
  sessionId: string,
  requestId: string,
  failedTaskId: string,
  reason: string,
  options: ApiCallOptions = {},
): Promise<McapRecheckResponse> {
  return visionLabApi.post<McapRecheckResponse>(
    `/submissions/${encodeURIComponent(sessionId)}/mcap-ingest/admin-retry`,
    { ...options, json: { requestId, failedTaskId, reason } },
  );
}
